/**
 * UploadAppointmentsDialog — a client's spreadsheet becomes a batch (#159).
 *
 * Pete, 2026-08-21: *"we need a way for the scheduler, when mass-creating the
 * appointments for the day, to have a spreadsheet of columns... so we can upload a
 * client supplied list but still create mass appointments."*
 *
 * ===========================================================================
 * ⛔ THREE STEPS, AND THE MIDDLE ONE IS THE POINT
 * ===========================================================================
 * Choose → PREVIEW → confirm. Pete asked for *"some functions to say the upload
 * looks good or is not correct and... confirmation after its uploaded"*, and the
 * preview is where a scheduler finds out that row 14 has no direction BEFORE forty
 * appointments exist rather than after.
 *
 * ⭐ THE PREVIEW IS THE SERVER'S, NOT THIS COMPONENT'S. Every verdict on screen was
 * decided server-side and stored; Confirm sends an upload id and nothing else. This
 * file cannot alter what gets created even by accident — see uploadRepository.ts.
 *
 * ⛔ AND IT DOES NOT CLOSE ON SUCCESS. Like MassCreateDialog, it switches to a
 * result view holding the batch and an Undo. Creating forty appointments is a big
 * enough act that "did that work, and can I take it back?" needs an answer that
 * stays on screen — a 4-second toast is not one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    AlertCircle, AlertTriangle, CheckCircle2, Download, FileSpreadsheet,
    Loader2, Undo2, Upload,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError,
    downloadUploadTemplate,
    previewAppointmentUpload,
    commitAppointmentUpload,
    discardAppointmentUpload,
    inspectClientSheet,
    backOutUpload,
    type UploadPreviewDto,
    type UploadCommitResult,
    type SheetInspectionDto,
} from '@/api/client';
import { SheetMapEditor } from '@/components/SheetMapEditor';

interface UploadAppointmentsDialogProps {
    open: boolean;
    onClose: () => void;
    facilityCode: string;
    /**
     * ⭐ The clients this Symbia site works with, for the picker (#174). ⚠️ May be
     * EMPTY — the dialog is not gated on lookups loading, because the picker is
     * optional and losing enrichment beats losing the upload.
     */
    clients: { ClientCode: string; ClientName: string }[];
    /** The day the calendar is showing — the sensible default for a blank Date cell. */
    localDate: string;
    /** Fired after a commit OR an undo, so the caller can reload the grid. */
    onChanged: () => void;
    getToken: () => Promise<string>;
}

type Stage = 'choose' | 'map' | 'preview' | 'done';

/**
 * ⚠️ A SENTINEL, BECAUSE RADIX SELECT REFUSES AN EMPTY-STRING ITEM VALUE — it uses
 * '' internally to mean "nothing selected", so an item carrying it throws. ⛔ It is
 * translated back to '' the moment it leaves the picker and never reaches the API.
 */
const NO_CLIENT = '__none__';

/**
 * ⚠️ THE SAME RADIX CONSTRAINT AS `NO_CLIENT`, for the tab picker (#132). '' means
 * "let the server decide as it always has", and Radix cannot hold '' as an item
 * value. ⛔ Translated back to '' before it leaves the picker; the API omits the
 * field entirely rather than sending a blank one.
 */
const ANY_SHEET = '__any__';

export function UploadAppointmentsDialog({
    open, onClose, facilityCode, clients, localDate, onChanged, getToken,
}: UploadAppointmentsDialogProps) {
    const [stage, setStage] = useState<Stage>('choose');
    const [file, setFile] = useState<File | null>(null);
    const [defaultDate, setDefaultDate] = useState(localDate);
    /**
     * ⭐ WHOSE LIST THIS IS (#174) — and the scheduler already knows, because
     * somebody sent it to them.
     *
     * ⛔ OPTIONAL ON PURPOSE. Naming the client is what fixes `ClientCode = 'TBD'`
     * on every uploaded appointment and what selects the WMS match rule, but a
     * scheduler holding a broker's mixed list genuinely may not have one answer —
     * and refusing the upload over it would push them back to typing forty
     * appointments by hand.
     */
    const [clientCode, setClientCode] = useState<string>('');
    const [status, setStatus] = useState<'APPROVED' | 'REQUESTED'>('APPROVED');
    const [preview, setPreview] = useState<UploadPreviewDto | null>(null);
    const [result, setResult] = useState<UploadCommitResult | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** ⭐ Set only when the SERVER has refused on limits. Never assumed up front. */
    const [limitBlock, setLimitBlock] = useState<string | null>(null);
    const [undone, setUndone] = useState(false);
    /**
     * ⭐ SET ONLY WHEN A CLIENT FILE COULD NOT BE READ AS OUR TEMPLATE (#174 part A).
     * ⛔ The scheduler is never asked up front whether this is a client's own file —
     * that is a question they should not have to answer, and getting it wrong would
     * either skip the template checks or refuse a perfectly good template.
     */
    const [inspection, setInspection] = useState<SheetInspectionDto | null>(null);
    /**
     * ⭐ THE TAB TO READ, AND THE TABS WE KNOW ABOUT (#132).
     *
     * ⛔ `sheetNames` IS LEARNED FROM THE FILE, NEVER GUESSED. Nothing in the browser
     * can name the tabs in an .xlsx without parsing it, so the picker appears only
     * once a read has told us — which is why it lives on the CHOOSE screen but is
     * populated by the preview that came back. ⚠️ A first attempt on a multi-tab
     * workbook is therefore still one click: check the file, then pick the tab.
     *
     * ⚠️ BOTH MUST DIE WITH THE FILE. A sheet name from workbook A sent for workbook
     * B either refuses a good file or — if the name happens to exist in both — reads
     * a tab nobody chose. See `clearFilePicker`.
     */
    const [sheetName, setSheetName] = useState<string>('');
    const [sheetNames, setSheetNames] = useState<string[]>([]);
    /**
     * ⭐ WHICH DAYS TO IMPORT (#135). Pete: *"there could be other dates you don't
     * want to inbound/outbound into the schedule."*
     *
     * ⛔ EMPTY MEANS "NOTHING TICKED YET", WHICH MEANS NO FILTER — the same
     * convention the API and the server use, end to end. It never means "import
     * nothing"; the screen has no way to express that and the server would not obey
     * it if it did.
     */
    const [importDates, setImportDates] = useState<string[]>([]);
    /**
     * ⭐ WHICH ROWS TO CREATE (#140). Pete: *"can we add a checkbox to each one so we
     * can select ALL or certain ones to create appointments for?"*
     *
     * ⛔ `null` MEANS "ALL VALID ROWS" AND IS THE OPENING STATE. A Set that starts
     * empty would mean the Create button did nothing until somebody ticked
     * something — silently changing what the button means for everyone who never
     * touches a checkbox. ⚠️ Null and "the user unticked everything" are different,
     * and only the first may mean all.
     */
    const [chosenRows, setChosenRows] = useState<Set<number> | null>(null);
    const fileInput = useRef<HTMLInputElement>(null);

    /**
     * ⛔ CLEARING REACT STATE IS NOT CLEARING THE INPUT, AND THIS BLOCKED A REAL
     * TEST. `setFile(null)` empties our state; the `<input type="file">` DOM node
     * still holds the previous selection in `.value`. ⚠️ A file input fires NO
     * change event when you pick the SAME file again — the value has not changed
     * — so after a refusal, "Choose another file" followed by re-picking that
     * same workbook did **literally nothing**: no event, no `setFile`, no error.
     *
     * Pete, 2026-08-24: *"on the second upload, I can't even pull in the same
     * spreadsheet to open it up."*
     *
     * ⭐ IT LOOKS LIKE A BACKEND HOLD AND IS NOT. The upload row written for a
     * refused file (`previewUpload` records reader fatals deliberately) is a
     * plausible-looking culprit — but it does not refuse a re-upload, and the
     * request never reached the server at all. **The give-away was that nothing
     * happened: no spinner, no message.** A server refusal would have said so.
     */
    const clearFilePicker = useCallback(() => {
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
        /**
         * ⛔ THE SCOPE BELONGS TO THE FILE, AND DIES WITH IT (#132/#135). A sheet
         * name or a set of ticked dates left over from the previous workbook is the
         * worst kind of stale state: it does not error, it silently narrows the new
         * file to a tab or a set of days the scheduler never chose for it — and the
         * screen would report the result as if they had.
         */
        setSheetName(''); setSheetNames([]); setImportDates([]);
        // ⛔ The ticks belong to the rows of THIS preview. Carried across a new file
        // they would select row numbers that mean something else entirely.
        setChosenRows(null);
    }, []);

    /**
     * Back to the file-choosing step, with the picker genuinely empty so the same
     * file can be chosen again. ⚠️ Every route back to 'choose' must go through
     * here — the reset-on-open effect only runs when `open` flips, which it does
     * not when a button inside the dialog moves the stage.
     */
    const backToChoose = useCallback(() => {
        clearFilePicker();
        setPreview(null); setError(null); setLimitBlock(null); setInspection(null);
        setStage('choose');
    }, [clearFilePicker]);

    /**
     * Leaving the mapper without saving.
     *
     * ⚠️ IT MUST NOT THROW THE FILE AWAY. Mapping is now reachable FROM a preview
     * ("I got the columns wrong"), so backing out of it has to land back on that
     * preview — not at an empty file picker with the upload to do again. ⛔ Only
     * when there is no preview to return to does this fall back to the start.
     */
    const cancelMapping = useCallback(() => {
        setInspection(null);
        if (preview) { setStage('preview'); return; }
        backToChoose();
    }, [preview, backToChoose]);

    // ⚠️ RESET ON OPEN, not on close. A dialog that clears itself while closing
    // flashes the empty state on the way out; more importantly, a scheduler who
    // reopens it must never see the previous file's verdicts as if they were this
    // file's — that is how the wrong batch gets confirmed.
    useEffect(() => {
        if (!open) return;
        setStage('choose'); clearFilePicker(); setDefaultDate(localDate);
        // ⛔ THE CLIENT IS CLEARED TOO, and that matters more than the rest: a
        // remembered client from the last file would attach THIS file's rows to the
        // previous customer, silently, on a screen nobody re-reads.
        setClientCode('');
        setStatus('APPROVED'); setPreview(null); setResult(null);
        setError(null); setLimitBlock(null); setUndone(false); setInspection(null);
    }, [open, localDate, clearFilePicker]);

    const getTemplate = useCallback(async () => {
        setBusy(true); setError(null);
        try {
            const { blob, fileName } = await downloadUploadTemplate(await getToken());
            /**
             * ⚠️ THE OBJECT URL IS REVOKED. Without it the blob is pinned in memory
             * for the life of the tab, and a scheduler downloading the template a
             * dozen times a day across a long shift leaks every copy.
             */
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = fileName;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not get the template.');
        } finally { setBusy(false); }
    }, [getToken]);

    /**
     * ⭐ THE SCOPE IS PASSED IN, NOT READ FROM STATE.
     *
     * ⚠️ A tick or a tab choice re-previews IMMEDIATELY, and `useState` does not
     * update the value this closure captured — so reading `importDates` here would
     * send the set as it was BEFORE the click, and the screen would come back showing
     * the previous selection while the checkbox showed the new one. ⛔ That is a
     * disagreement between what is ticked and what was imported, on the one screen
     * whose entire job is to say which days were taken.
     */
    const doPreview = useCallback(async (
        scope?: { sheetName?: string; importDates?: string[] },
    ) => {
        if (!file) return;
        setBusy(true); setError(null); setInspection(null);
        try {
            const p = await previewAppointmentUpload(
                await getToken(), facilityCode, defaultDate, file, clientCode || null,
                {
                    sheetName: scope?.sheetName ?? sheetName,
                    importDates: scope?.importDates ?? importDates,
                },
            );
            setPreview(p);
            // ⛔ A NEW PREVIEW IS A NEW SET OF ROWS. Row 198 in one read is not row
            // 198 in the next once a different day or tab is chosen.
            setChosenRows(null);
            /**
             * ⭐ THE TABS, LEARNED FROM THE READ (#132). ⛔ Kept even when the read
             * FAILED — a multi-sheet refusal is exactly the moment the picker is
             * needed, and throwing the names away on the error path would leave the
             * scheduler reading tab names out of a sentence again.
             *
             * ⚠️ ONLY WHEN THE SERVER ACTUALLY KNOWS. An empty list means the
             * workbook never opened; overwriting a good list with it would make the
             * picker vanish on an unrelated failure.
             */
            if (p.sheetNames.length > 0) setSheetNames(p.sheetNames);
            setStage('preview');
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'The upload failed.');
        } finally { setBusy(false); }
    }, [file, facilityCode, defaultDate, clientCode, sheetName, importDates, getToken]);

    /**
     * ⭐ OFFER TO LEARN THE FILE — reached from the preview's fatal, never guessed at.
     *
     * ⛔ ONLY WHEN A CLIENT IS NAMED. A mapping is stored against a client, so
     * without one there is nothing to save it to; and reading an unknown file with
     * no client would be the generic parse this whole design refuses.
     */
    const doInspect = useCallback(async () => {
        if (!file || !clientCode) return;
        setBusy(true); setError(null);
        try {
            const i = await inspectClientSheet(await getToken(), facilityCode, file, clientCode);
            if (i.fatal) { setError(i.fatal); return; }
            setInspection(i);
            setStage('map');
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'We could not read that file.');
        } finally { setBusy(false); }
    }, [file, facilityCode, clientCode, getToken]);

    /**
     * ⭐ THE VALID ROW NUMBERS, AND HOW MANY ARE TICKED (#140).
     *
     * ⛔ ONLY VALID ROWS ARE SELECTABLE. A rejected row cannot be created, so a
     * checkbox on it would promise something the commit will not honour — it is
     * shown, disabled, so the row is still visible with its errors.
     */
    const validRowNumbers = (preview?.rows ?? []).filter(r => r.ok).map(r => r.rowNumber);
    const isChosen = (n: number) => chosenRows === null || chosenRows.has(n);
    const chosenCount = chosenRows === null ? validRowNumbers.length
        : validRowNumbers.filter(n => chosenRows.has(n)).length;
    const allChosen = chosenCount === validRowNumbers.length;

    const toggleRow = (n: number) => {
        // ⚠️ `null` means all — materialise it before removing one, or the first
        // untick would read as "nothing was selected, now select this one".
        const next = new Set(chosenRows ?? validRowNumbers);
        if (next.has(n)) next.delete(n); else next.add(n);
        setChosenRows(next);
    };
    const toggleAll = () => setChosenRows(allChosen ? new Set<number>() : null);

    /**
     * ⛔ THE BUTTON COUNTS WHAT IS TICKED, NOT WHAT THE FILE HOLDS. Pete, 2026-08-24:
     * *"don't forget to recreate the 'Create 41 appointments' button with the number
     * selected."* ⚠️ A button promising 41 while 12 are ticked is the screen lying
     * about an irreversible act.
     */
    const doCommit = useCallback(async (acknowledge: boolean) => {
        if (!preview) return;
        setBusy(true); setError(null);
        try {
            const r = await commitAppointmentUpload(await getToken(), preview.uploadId, {
                status,
                acknowledgeDailyLimit: acknowledge,
                /**
                 * ⛔ OMITTED WHEN EVERYTHING IS TICKED, not sent as the full list.
                 * Absent means "all valid rows" to every layer below, and sending
                 * the complete list would be a different request that happens to
                 * mean the same thing — until the server's set and the screen's
                 * disagree, at which point the screen silently wins.
                 */
                ...(chosenRows === null || allChosen
                    ? {}
                    : { rowNumbers: validRowNumbers.filter(n => chosenRows.has(n)) }),
            });
            setResult(r);
            setStage('done');
            onChanged();
        } catch (err) {
            if (err instanceof ApiCallError && err.details.code === 'daily_limit_reached') {
                // ⭐ THE SERVER'S OWN SENTENCE, not a re-worded one. It names the
                // number, the scope and the date; two versions of one rule would
                // drift the first time the wording changed.
                setLimitBlock(err.details.error);
            } else if (err instanceof ApiCallError) {
                setError(err.details.error);
            } else {
                setError('The commit failed.');
            }
        } finally { setBusy(false); }
    }, [preview, status, getToken, onChanged, chosenRows, allChosen, validRowNumbers]);

    const doDiscard = useCallback(async () => {
        if (!preview) { onClose(); return; }
        setBusy(true);
        try {
            // ⭐ A DISCARD IS RECORDED, not a silent close. "We looked at that file
            // and chose not to" is a thing the log should be able to answer.
            await discardAppointmentUpload(await getToken(), preview.uploadId);
        } catch { /* closing anyway — a lost discard must not trap the scheduler */ }
        finally { setBusy(false); onClose(); }
    }, [preview, getToken, onClose]);

    const doUndo = useCallback(async () => {
        if (!preview) return;
        setBusy(true); setError(null);
        try {
            /**
             * ⛔ REMOVES, DOES NOT CANCEL (fixed 2026-08-24).
             *
             * ⚠️ THIS CALLED `cancelMassCreateBatch` — #66's batch cancel — so Undo
             * set the appointments to CANCELLED and left them on the board. Pete,
             * on the first real use: *"did Undo and it cancelled the appointment,
             * not removed it."* The audit line read `MassCreateCancelled`, which is
             * exactly what it did.
             *
             * ⭐ IT NOW USES THE SAME PATH AS "Back out upload" on the calendar, which
             * is what Pete asked for both of them to do: *"back them out completely,
             * not make them Cancelled... we don't get too complicated on appointment
             * status'."* ⛔ TWO BUTTONS THAT BOTH SAY "UNDO THIS UPLOAD" MUST NOT MEAN
             * DIFFERENT THINGS — one leaving tombstones and one not is the kind of
             * difference nobody discovers until they are reconciling a day.
             *
             * ⚠️ AND IT INHERITS THE THREE-WAY RULE. Untouched rows are deleted,
             * worked-on rows are CANCELLED so their record survives, and a truck that
             * is on site or has been and gone is left alone — each named on screen.
             */
            const r = await backOutUpload(
                await getToken(), preview.uploadId, facilityCode, defaultDate);
            setUndone(true);
            // ⛔ THE REFUSED ONES ARE SAID OUT LOUD. "Undone" over the top of two
            // appointments that are still live is the silent half-answer this whole
            // feature exists to avoid.
            if (r.kept.length > 0) setError(r.message);
            onChanged();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'The undo failed.');
        } finally { setBusy(false); }
    }, [preview, facilityCode, defaultDate, getToken, onChanged]);

    // ⛔ ERRORS BLOCK, WARNINGS DO NOT — the same severities the booking form uses.
    const blocking = (preview?.preflight ?? []).filter(f => f.severity === 'error');
    const advisories = (preview?.preflight ?? []).filter(f => f.severity === 'warning');
    const canCommit = !!preview && !preview.fatal && chosenCount > 0 && blocking.length === 0;

    /**
     * ⭐ THE WMS MATCHES (#174), BY ROW.
     *
     * ⚠️ ONLY THE ROWS THAT ACTUALLY GOT AN ORDER NUMBER COUNT. A verdict of
     * Ambiguous or WrongClient is already reported as an advisory above; counting it
     * here would put it on screen twice, once as good news.
     *
     * ⛔ THE MATCH NEVER GATES `canCommit` and never will. An unmatched row is a
     * truck whose receipt has not been made yet — Pete's own case — and the whole
     * point of scheduling it is that it is coming anyway.
     */
    const matchByRow = new Map((preview?.matches ?? []).map(m => [m.rowNumber, m]));
    const matchedCount = (preview?.matches ?? []).filter(m => m.orderNumber).length;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
            <DialogContent className={stage === 'map' ? 'max-w-5xl' : 'max-w-3xl'}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        Upload appointments
                    </DialogTitle>
                    <DialogDescription>
                        {/* ⭐ SAID OUT LOUD, EVERY TIME. Pete's own framing: an ASN or PO
                            through the WMS is the proper route and this is the fallback.
                            A screen that does not say so lets the fallback quietly
                            become the default. */}
                        For a client-supplied list. An ASN or PO through the WMS is the
                        better route and needs no file at all.
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                        <div>{error}</div>
                    </div>
                )}

                {/* ================= CHOOSE ================= */}
                {stage === 'choose' && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="up-date">Date for rows with no date</Label>
                                <Input
                                    id="up-date" type="date" value={defaultDate}
                                    onChange={(e) => setDefaultDate(e.target.value)}
                                />
                                <p className="text-muted-foreground text-xs">
                                    Used only where the Date column is blank.
                                </p>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="up-status">Create as</Label>
                                <Select value={status} onValueChange={(v) => setStatus(v as 'APPROVED' | 'REQUESTED')}>
                                    <SelectTrigger id="up-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="APPROVED">Approved</SelectItem>
                                        <SelectItem value="REQUESTED">Requested</SelectItem>
                                    </SelectContent>
                                </Select>
                                {/* ⛔ CHOSEN HERE, NEVER IN THE SHEET. A Status column
                                    would let whoever typed the spreadsheet — including a
                                    client — grant themselves an approved appointment. */}
                                <p className="text-muted-foreground text-xs">
                                    {status === 'APPROVED'
                                        ? 'Symbia has already agreed to these trucks.'
                                        : 'These still need reviewing before they are confirmed.'}
                                </p>
                            </div>
                        </div>

                        {/* ---- ⭐ WHOSE LIST THIS IS (#174) ---- */}
                        <div className="space-y-1.5">
                            <Label htmlFor="up-client">Client (optional)</Label>
                            <Select
                                value={clientCode}
                                onValueChange={(v) => setClientCode(v === NO_CLIENT ? '' : v)}
                            >
                                <SelectTrigger id="up-client">
                                    <SelectValue placeholder="Not sure / mixed list" />
                                </SelectTrigger>
                                <SelectContent>
                                    {/* ⛔ AN EXPLICIT "I DO NOT KNOW", not just an
                                        unopened picker. Radix Select cannot hold an
                                        empty-string value, and more to the point a
                                        scheduler needs a way to UNDO a wrong choice
                                        without closing the dialog. */}
                                    <SelectItem value={NO_CLIENT}>Not sure / mixed list</SelectItem>
                                    {clients.map((c) => (
                                        <SelectItem key={c.ClientCode} value={c.ClientCode}>
                                            {c.ClientName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-muted-foreground text-xs">
                                {clientCode
                                    ? 'These appointments will belong to this client, and we will '
                                      + 'look their numbers up in the WMS.'
                                    : 'Without a client the appointments are created but belong to '
                                      + 'nobody — they will not show in a client portal and we cannot '
                                      + 'look their numbers up in the WMS.'}
                            </p>
                        </div>

                        {/* ---- ⭐ THE FILE, AND THE TEMPLATE BESIDE IT ----
                             Pete, 2026-08-22: *"just think you need the Template button at
                             the bottom of the screen next to check the file. Its confusing
                             at the top."*

                             ⭐ HE IS RIGHT, AND THE REASON IS ORDER OF OPERATIONS. A
                             download button ABOVE the form reads as step one — as though
                             every upload begins by fetching a template. It does not: most
                             uploads are a file the scheduler already has, and the template
                             is what they reach for only when they DON'T. Putting it beside
                             the file picker makes it an alternative to choosing a file
                             rather than a prerequisite for it. */}
                        <div className="space-y-1.5">
                            <Label htmlFor="up-file">The file</Label>
                            <Input
                                id="up-file" ref={fileInput} type="file"
                                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                /* ⛔ A NEW FILE RESETS THE SCOPE. The tab and the
                                   ticked days were chosen against the PREVIOUS
                                   workbook; carrying them over silently narrows this
                                   one to a selection nobody made for it. ⚠️ Not
                                   routed through `clearFilePicker` — that also blanks
                                   the DOM input, which would undo the pick we are
                                   handling. */
                                onChange={(e) => {
                                    setSheetName(''); setSheetNames([]); setImportDates([]);
                                    setFile(e.target.files?.[0] ?? null);
                                }}
                            />

                            {/* ---- ⭐ WHICH TAB (#132) ----
                                 Pete was offered better refusal wording and chose a
                                 picker. ⛔ IT APPEARS ONLY ONCE WE HAVE READ THE FILE,
                                 because nothing in the browser can name the tabs in an
                                 .xlsx without parsing it — and shipping a spreadsheet
                                 parser into the bundle to avoid one round trip would be
                                 a second reader with its own opinions about dates.
                                 ⚠️ So the first check of a multi-tab workbook still
                                 refuses; what changed is that it now comes back with a
                                 list, and the second attempt is one click. */}
                            {sheetNames.length > 1 && (
                                <div className="space-y-1.5 pt-1">
                                    <Label htmlFor="up-sheet">Which tab?</Label>
                                    <Select
                                        value={sheetName || ANY_SHEET}
                                        onValueChange={(v) => setSheetName(v === ANY_SHEET ? '' : v)}
                                    >
                                        <SelectTrigger id="up-sheet"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {/* ⚠️ NOT "the first sheet" — the server
                                                looks for a tab called Appointments
                                                first and only then falls back, and a
                                                label that misstates the rule teaches
                                                the wrong lesson the day it matters. */}
                                            <SelectItem value={ANY_SHEET}>Decide for me</SelectItem>
                                            {sheetNames.map(n => (
                                                <SelectItem key={n} value={n}>{n}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-muted-foreground text-xs">
                                        This workbook has {sheetNames.length} tabs. Choosing one
                                        reads it as our template — the version stamp and the
                                        column check still apply to whichever tab you pick.
                                    </p>
                                </div>
                            )}
                            {/* ⚠️ BOTH ROUTES NAMED, WITH A PREFERENCE STATED — Pete:
                                *"maybe state its preferred that we use the template to
                                upload but you can also upload a clients template by
                                choosing that file below."*

                                ⛔ THE PREFERENCE IS REAL, NOT POLITENESS. Our template
                                carries a version stamp, so a file whose columns moved is
                                REFUSED rather than misread. A client's own sheet has no
                                such stamp — its guard is the saved header set, which only
                                exists once somebody has mapped it. ⭐ Saying which is
                                preferred, and why the other still works, is the difference
                                between a scheduler feeling stuck and feeling offered a
                                choice. */}
                            <p className="text-muted-foreground text-xs">
                                Our template is best — it is checked column by column, so a
                                file that has drifted is refused rather than misread. You can
                                also upload a client&rsquo;s own spreadsheet: choose it here,
                                pick their name above, and we will ask you to map its columns
                                once.
                            </p>
                        </div>

                        <DialogFooter>
                            {/* ⭐ TEMPLATE ON THE LEFT, ACTION ON THE RIGHT. Same row, so
                                the two ways of getting a file to check sit together — but
                                separated, because one of them ends the step and the other
                                does not. */}
                            <div className="flex w-full items-center justify-between gap-2">
                                <Button variant="outline" size="sm"
                                        onClick={() => void getTemplate()} disabled={busy}>
                                    <Download className="h-3.5 w-3.5" /> Download our template
                                </Button>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
                                    <Button size="sm" onClick={() => void doPreview()} disabled={!file || busy}>
                                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                        <Upload className="h-3.5 w-3.5" /> Check the file
                                    </Button>
                                </div>
                            </div>
                        </DialogFooter>
                    </div>
                )}

                {/* ================= ⭐ MAP (#174 part A) ================= */}
                {stage === 'map' && inspection && (
                    <SheetMapEditor
                        inspection={inspection}
                        facilityCode={facilityCode}
                        clientCode={clientCode}
                        clientName={clients.find(c => c.ClientCode === clientCode)?.ClientName
                                    ?? clientCode}
                        getToken={getToken}
                        // ⭐ STRAIGHT BACK INTO THE PREVIEW. The scheduler came here to
                        // upload a file, not to configure something — the mapping is a
                        // detour and the flow should close it themselves.
                        onSaved={() => { setInspection(null); void doPreview(); }}
                        onCancel={cancelMapping}
                    />
                )}

                {/* ================= PREVIEW ================= */}
                {stage === 'preview' && preview && (
                    <div className="space-y-3">
                        {/* ---- ⭐ WHICH TAB WE READ, AND A WAY TO CHANGE IT (#132) ----

                             ⛔ ABOVE THE FATAL/NOT-FATAL SPLIT ON PURPOSE. The refusal
                             this exists to fix — "this workbook has 3 sheets and none
                             is called Appointments" — IS a fatal, so a picker rendered
                             only in the healthy branch would be absent from the one
                             screen that needs it. ⚠️ And it is equally useful on a
                             clean read: a file that parsed perfectly from `Sheet1` may
                             still be the wrong tab, and nobody can notice that unless
                             the screen says which one we took. */}
                        {sheetNames.length > 1 && (
                            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-2 text-xs">
                                <span className="text-muted-foreground">
                                    {preview.sheetName
                                        ? <>Read from the <strong>{preview.sheetName}</strong> tab.</>
                                        : <>This workbook has {sheetNames.length} tabs.</>}
                                </span>
                                <Select
                                    value={sheetName || ANY_SHEET}
                                    onValueChange={(v) => {
                                        const next = v === ANY_SHEET ? '' : v;
                                        setSheetName(next);
                                        /* ⚠️ PASSED IN, NOT READ BACK FROM STATE — the
                                           setter above has not landed yet. See doPreview.
                                           ⛔ AND THE DATE TICKS ARE CLEARED: they name days
                                           found in the OTHER tab, and carrying them across
                                           would filter this one to days it may not hold. */
                                        setImportDates([]);
                                        void doPreview({ sheetName: next, importDates: [] });
                                    }}
                                >
                                    <SelectTrigger className="h-7 w-44 text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ANY_SHEET}>Decide for me</SelectItem>
                                        {sheetNames.map(n => (
                                            <SelectItem key={n} value={n}>{n}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            </div>
                        )}

                        {/* ⛔ A WHOLE-FILE REFUSAL REPLACES THE TABLE. Showing rows
                            underneath a fatal message invites somebody to read past it. */}
                        {preview.fatal ? (
                            <div className="space-y-3">
                                <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                                    <div>
                                        <div className="font-medium">We cannot read this file</div>
                                        <div className="mt-0.5">{preview.fatal}</div>
                                    </div>
                                </div>

                                {/* ---- ⭐ THE OFFER THAT REPLACES THE EMAIL (#174 part A) ----
                                     Pete: *"then we don't have the back and forth with the
                                     client to get them to do the spreadsheet correctly!"*

                                     ⛔ OFFERED ONLY WITH A CLIENT NAMED. A mapping is stored
                                     against a client, so without one there is nothing to save
                                     it to — and reading an unknown file with no client would be
                                     exactly the generic parse this design refuses. */}
                                {clientCode ? (
                                    <div className="rounded-md border bg-muted/30 p-3 text-sm">
                                        <div className="font-medium">
                                            Is this {clients.find(c => c.ClientCode === clientCode)?.ClientName
                                                     ?? clientCode}&rsquo;s own spreadsheet?
                                        </div>
                                        <div className="text-muted-foreground mt-0.5 text-xs">
                                            We can learn their column layout once and read it
                                            directly from then on — no need to ask them to use our
                                            template.
                                        </div>
                                        <Button className="mt-2" size="sm" variant="outline"
                                                onClick={() => void doInspect()} disabled={busy}>
                                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                            Map their columns
                                        </Button>
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-xs">
                                        If this is a client&rsquo;s own spreadsheet rather than our
                                        template, go back and choose the client — we can then learn
                                        their layout instead of asking them to change it.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-wrap gap-3 text-sm">
                                    <Badge tone="ok">{preview.rowsValid} ready</Badge>
                                    {preview.rowsRejected > 0 && (
                                        <Badge tone="bad">{preview.rowsRejected} rejected</Badge>
                                    )}
                                    {/* ⚠️ NEVER SILENT. A truncated file reads as "everything
                                        was uploaded" to whoever looks at it later. */}
                                    {preview.rowsDropped > 0 && (
                                        <Badge tone="warn">{preview.rowsDropped} past the row limit</Badge>
                                    )}
                                    {/* ⭐ SHOWN ONLY WHEN THERE IS SOMETHING TO SHOW.
                                        ⛔ There is deliberately no "0 matched" badge:
                                        an unmatched file is the ordinary case for a
                                        client with no rule, and a zero on screen reads
                                        as a failure somebody should chase. */}
                                    {matchedCount > 0 && (
                                        <Badge tone="ok">{matchedCount} found in the WMS</Badge>
                                    )}
                                </div>

                                {/* ---- ⭐ WHICH DAYS TO IMPORT (#135) ----

                                     Pete, 2026-08-24: *"you need to select a date or date
                                     range on the items you want to inbound — there could be
                                     other dates you don't want to inbound/outbound into the
                                     schedule."* He was offered a range and picked *"tick the
                                     dates found"*.

                                     ⛔ THE OLD SCREEN SAID "change the date and upload it
                                     again for those" — which is the feature admitting it
                                     could not do the thing being asked. A file holding
                                     Monday, Tuesday and Thursday needed three uploads, and
                                     every one of them re-read 1,208 rows to keep forty.

                                     ⚠️ ONLY WHEN THERE IS A CHOICE TO MAKE. One date found
                                     is not a decision, and a tick list of one teaches people
                                     to click past it. */}
                                {/* ---- ⛔ THE DAY COLUMN IS THE WRONG COLUMN ----

                                     ⚠️ THIS SHIPPED WRONG AND PETE CAUGHT IT ON THE FIRST
                                     REAL FILE, 2026-08-24: *"There should only be 41
                                     appointments for 8/24/2026. Not sure why its not showing
                                     the right number of the right dates."* The mapping's day
                                     column pointed at Thornova's `Delivery appt` — the TIME
                                     column, holding `drop` and `700`…`1600` — so the tick
                                     list offered twelve days spread across 1901-1904.

                                     ⛔ THE PREVIEW TABLE COULD NOT SHOW THIS. It renders the
                                     mapped Appointment Date; the filter was reading a
                                     different column entirely, and nothing on screen said so.
                                     ⭐ A blank cell is ordinary — it means "use the date at the
                                     top". A cell holding `drop` is a WRONG COLUMN, and that is
                                     the only number worth alarming on. */}
                                {preview.dates.rowsNotADate > 0 && (
                                    <Note tone="bad" title="The day column looks like the wrong column">
                                        {preview.dates.rowsNotADate} row(s) have something in the
                                        day column that is not a date
                                        {preview.dates.notADateExamples.length > 0 && (
                                            <> — for example{' '}
                                            {preview.dates.notADateExamples
                                                .map(v => `"${v}"`).join(', ')}</>
                                        )}.
                                        <div className="mt-1.5 text-xs">
                                            Press <strong>Change mapping</strong> and point the day
                                            column at the column holding the delivery date. Until
                                            then the days offered below are not this file&rsquo;s
                                            real dates.
                                        </div>
                                    </Note>
                                )}

                                {/* ⚠️ Two different columns for one date. Almost always a
                                    mis-mapping, and invisible from the preview table. */}
                                {preview.dates.dateFilterIsOverride && (
                                    <Note tone="warn" title="The day column is not the Appointment Date column">
                                        This mapping picks the day from one column and the
                                        appointment date from another, so ticking a day selects
                                        rows by one fact and dates them by a different one. Change
                                        the mapping unless that is genuinely what this client&rsquo;s
                                        file needs.
                                    </Note>
                                )}

                                {preview.dates.seen.length > 1 && (
                                    <DateScope
                                        seen={preview.dates.seen}
                                        /* ⛔ EMPTY `imported` MEANS NO FILTER RAN — every
                                           day was read. It does NOT mean nothing was
                                           imported, and rendering it as an empty tick list
                                           would say the opposite of the truth. */
                                        imported={preview.dates.imported}
                                        rowsOtherDates={preview.dates.rowsOtherDates}
                                        rowsNoDate={preview.dates.rowsNoDate}
                                        rowsBlankDateDropped={preview.dates.rowsBlankDateDropped}
                                        busy={busy}
                                        onChange={(next) => {
                                            setImportDates(next);
                                            // ⚠️ Passed in — see doPreview. The tab is left
                                            // alone; only the day scope changed.
                                            void doPreview({ importDates: next });
                                        }}
                                    />
                                )}

                                {/* ⭐ Their file changed but nothing we read moved. Said, not
                                    warned about — it is information, not a problem. */}
                                {preview.viaMapping?.driftMessage && (
                                    <p className="text-muted-foreground text-xs">
                                        {preview.viaMapping.driftMessage}
                                    </p>
                                )}

                                {/* ⚠️ THE ONE THING WORTH SAYING ABOUT NO CLIENT, said
                                    where the consequence lands rather than only on the
                                    previous screen. Not an error — the file is fine. */}
                                {!preview.clientCode && (
                                    <Note tone="warn" title="No client was chosen">
                                        These appointments will be created without a client, so they
                                        will not appear in a client portal and their numbers were not
                                        looked up in the WMS. Go back if you know whose list this is.
                                    </Note>
                                )}

                                {/* ⭐ THE SAME FILE, ALREADY COMMITTED. A scheduler who was
                                    not sure the first upload worked WILL send it again —
                                    that is care, not error. Told, not blocked. */}
                                {preview.duplicateOf && (
                                    <Note tone="warn" title="This exact file was already committed here">
                                        {new Date(preview.duplicateOf.uploadedUtc).toLocaleString()} —{' '}
                                        {preview.duplicateOf.rowsValid} appointment(s). Uploading it
                                        again will create them a second time.
                                    </Note>
                                )}

                                {blocking.length > 0 && (
                                    <Note tone="bad" title={`${blocking.length} row(s) cannot be placed`}>
                                        <ul className="mt-1 space-y-0.5">
                                            {blocking.slice(0, 6).map((f, i) => (
                                                <li key={i}>Row {f.rowNumber}: {f.detail}</li>
                                            ))}
                                        </ul>
                                        {/* ⛔ NOT OVERRIDABLE, and the screen says why rather
                                            than offering a button that would fail. */}
                                        <div className="mt-1.5 text-xs">
                                            A double-booked door is an error on the booking form too,
                                            so it cannot be confirmed past. Change the door or the
                                            time in the sheet.
                                        </div>
                                    </Note>
                                )}

                                {advisories.map((f, i) => (
                                    <Note key={i} tone="warn" title={f.rowNumber ? `Row ${f.rowNumber}` : 'Worth knowing'}>
                                        {f.detail}
                                    </Note>
                                ))}

                                {preview.limitWarnings.map((w, i) => (
                                    <Note key={i} tone="warn" title="This crosses the day's limit">
                                        {w.message}
                                    </Note>
                                ))}

                                {/* ---- the rows ---- */}
                                <div className="max-h-72 overflow-auto rounded-md border">
                                    <table className="w-full text-xs">
                                        <thead className="bg-muted/50 sticky top-0">
                                            <tr>
                                                {/* ⭐ #140 — all / none. ⚠️ Only the VALID
                                                    rows are ever counted or toggled. */}
                                                <th className="px-2 py-1.5 text-left font-medium">
                                                    <input
                                                        type="checkbox"
                                                        aria-label={allChosen ? 'Deselect all rows' : 'Select all rows'}
                                                        checked={allChosen}
                                                        onChange={toggleAll}
                                                        disabled={busy || validRowNumbers.length === 0}
                                                    />
                                                </th>
                                                <th className="px-2 py-1.5 text-left font-medium">Row</th>
                                                <th className="px-2 py-1.5 text-left font-medium">Dir</th>
                                                <th className="px-2 py-1.5 text-left font-medium">Date</th>
                                                <th className="px-2 py-1.5 text-left font-medium">Time</th>
                                                <th className="px-2 py-1.5 text-left font-medium">Door</th>
                                                {/* ⭐ NAMED BY WHAT IT HOLDS — see referenceHeading. */}
                                                <th className="px-2 py-1.5 text-left font-medium">
                                                    {referenceHeading(preview.rows)}
                                                </th>
                                                {/* ⭐ THE COLUMN APPEARS ONLY WHEN THERE
                                                    ARE MATCHES. An always-present column
                                                    full of dashes teaches a scheduler that
                                                    the WMS never knows anything. */}
                                                {matchedCount > 0 && (
                                                    <th className="px-2 py-1.5 text-left font-medium">WMS</th>
                                                )}
                                                <th className="px-2 py-1.5 text-left font-medium">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.rows.map((r) => (
                                                <tr key={r.rowNumber} className={r.ok ? '' : 'bg-destructive/5'}>
                                                    {/* ⛔ A REJECTED ROW IS SHOWN BUT NOT
                                                        TICKABLE. It cannot be created, so a
                                                        live checkbox would promise something
                                                        the commit will not honour. */}
                                                    <td className="px-2 py-1 align-top">
                                                        <input
                                                            type="checkbox"
                                                            aria-label={`Create row ${r.rowNumber}`}
                                                            checked={r.ok && isChosen(r.rowNumber)}
                                                            disabled={!r.ok || busy}
                                                            onChange={() => toggleRow(r.rowNumber)}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-1 align-top tabular-nums">{r.rowNumber}</td>
                                                    <td className="px-2 py-1 align-top">{str(r.values?.direction)}</td>
                                                    <td className="px-2 py-1 align-top">{str(r.values?.appointmentDate)}</td>
                                                    {/* ⚠️ Blank time is not missing data — it is a
                                                        day-only appointment, and saying so stops a
                                                        scheduler "fixing" it. */}
                                                    <td className="px-2 py-1 align-top">
                                                        {r.values?.appointmentTime
                                                            ? str(r.values.appointmentTime)
                                                            : <span className="text-muted-foreground">day only</span>}
                                                    </td>
                                                    <td className="px-2 py-1 align-top">{str(r.values?.dockDoor)}</td>
                                                    <td className="px-2 py-1 align-top">
                                                        {referenceOf(r.values).value}
                                                    </td>
                                                    {matchedCount > 0 && (
                                                        <td className="px-2 py-1 align-top tabular-nums">
                                                            {matchByRow.get(r.rowNumber)?.orderNumber
                                                                ?? <span className="text-muted-foreground">—</span>}
                                                        </td>
                                                    )}
                                                    <td className="px-2 py-1 align-top">
                                                        {r.errors.map((e, i) => (
                                                            <div key={i} className="text-destructive">{e}</div>
                                                        ))}
                                                        {r.warnings.map((w, i) => (
                                                            <div key={i} className="text-amber-600">{w}</div>
                                                        ))}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ⚠️ SAID AT THE MOMENT OF DECISION. A rejected row is not
                                    created — a scheduler who assumes "it will sort itself
                                    out" is short some trucks and does not know it. */}
                                {preview.rowsRejected > 0 && blocking.length === 0 && (
                                    <p className="text-muted-foreground text-xs">
                                        Confirming creates the {chosenCount} selected row(s) only.
                                        The rejected ones are not created — fix the sheet and upload
                                        again for those.
                                    </p>
                                )}

                                {limitBlock && (
                                    <Note tone="warn" title="The server refused on the day's limit">
                                        {limitBlock}
                                        <div className="mt-1.5 text-xs">
                                            The limit exists so other customers can still book. Create
                                            anyway only if Symbia has agreed to these trucks.
                                        </div>
                                    </Note>
                                )}
                            </>
                        )}

                        <DialogFooter>
                            <div className="flex w-full flex-wrap items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button variant="ghost" size="sm" onClick={backToChoose} disabled={busy}>
                                        Choose another file
                                    </Button>

                                    {/* ---- ⭐ MAPPING IS REACHABLE FROM ANY PREVIEW ----
                                        ⛔ IT USED TO LIVE ONLY INSIDE `preview.fatal`, and that
                                        was the bug. Pete, 2026-08-24: *"won't let me get to the
                                        mapping screen now?"* — his file READ perfectly well as
                                        our template, so nothing fatalled; it just rejected all
                                        500 rows for a missing Direction. ⚠️ A file we can PARSE
                                        but are reading WRONGLY is the exact case the mapper is
                                        for, and it was the one case that could not reach it.

                                        ⭐ The client picker is inline here rather than a trip
                                        back to the start: the old copy said "go back and choose
                                        the client", which threw away the file and the preview to
                                        do something that belongs right here. */}
                                    {clientCode ? (
                                        <Button variant="outline" size="sm"
                                                onClick={() => void doInspect()} disabled={busy}>
                                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                            {preview.viaMapping ? 'Change mapping' : 'Map their columns'}
                                        </Button>
                                    ) : clients.length > 0 && (
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-muted-foreground text-xs">
                                                Client&rsquo;s own sheet?
                                            </span>
                                            <Select value={NO_CLIENT}
                                                    onValueChange={(v) => setClientCode(v === NO_CLIENT ? '' : v)}>
                                                <SelectTrigger className="h-8 w-44 text-xs">
                                                    <SelectValue placeholder="Choose client…" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value={NO_CLIENT}>Choose client…</SelectItem>
                                                    {clients.map(c => (
                                                        <SelectItem key={c.ClientCode} value={c.ClientCode}>
                                                            {c.ClientName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => void doDiscard()} disabled={busy}>
                                        Discard
                                    </Button>
                                    {limitBlock ? (
                                        /* ⛔ A SEPARATE BUTTON, never a checkbox left ticked
                                           from last time. The acknowledgement IS this click. */
                                        <Button variant="destructive" size="sm"
                                                onClick={() => void doCommit(true)} disabled={busy}>
                                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                            Create anyway
                                        </Button>
                                    ) : (
                                        <Button size="sm" onClick={() => void doCommit(false)}
                                                disabled={!canCommit || busy}>
                                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                            {/* ⛔ THE COUNT IS WHAT IS TICKED. Pete: *"don't
                                                forget to recreate the button with the number
                                                selected."* A button promising 41 while 12 are
                                                ticked is the screen lying about an
                                                irreversible act. */}
                                            {/* ⛔ THE COUNT IS WHAT IS TICKED (#140). Pete:
                                                *"don't forget to recreate the button with the
                                                number selected."* A button promising 41 while
                                                12 are ticked is the screen lying about an
                                                irreversible act. */}
                                            Create {chosenCount} appointment{chosenCount === 1 ? '' : 's'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </DialogFooter>
                    </div>
                )}

                {/* ================= DONE ================= */}
                {stage === 'done' && result && (
                    <div className="space-y-3">
                        <div className="flex gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                            <div>
                                {/* ⛔ THE WORD HAD TO CHANGE WITH THE BEHAVIOUR. It said
                                    "cancelled", which was accurate of the old batch-cancel;
                                    saying it now, when the rows are deleted, would describe an
                                    act that no longer happens. ⚠️ Wording that outlives its
                                    code is how a screen starts lying quietly. */}
                                <div className="font-medium">
                                    {undone
                                        ? 'Undone — the appointments were removed.'
                                        : `Created ${result.created} appointment${result.created === 1 ? '' : 's'}.`}
                                </div>
                                <div className="text-muted-foreground mt-0.5 text-xs">
                                    Batch {result.batchId?.slice(0, 8) ?? '—'}
                                </div>
                            </div>
                        </div>

                        {/* ⚠️ A DOOR THAT VANISHED BETWEEN PREVIEW AND COMMIT. Silent,
                            this is a difference between what the sheet says and what
                            the board shows that nobody finds until the truck is here. */}
                        {/* ⛔ OPTIONAL-CHAINED, AND THAT IS NOT DEFENSIVE CLUTTER.
                            `result.doorsDropped.length` on an `undefined` THREW here
                            and white-screened production on the first successful
                            commit (2026-08-24) — the route was not forwarding the
                            field, TypeScript was happy on both sides, and React
                            unmounted the entire app. ⚠️ The route is fixed and a guard
                            now checks it; this is the second line of defence, because
                            a blank page is a catastrophic answer to a missing array. */}
                        {(result.doorsDropped?.length ?? 0) > 0 && (
                            <Note tone="warn" title="Some doors were not assigned">
                                Row {result.doorsDropped?.join(', row ')} named a door that is no
                                longer active, so those appointments were created without one.
                            </Note>
                        )}

                        {/* ⛔ THE DRIVER GATE, SAID OUT LOUD. Silence here would be
                            the BOL bug again: details a client supplied, shown on the
                            preview, then quietly not there. */}
                        {(result.driversSkipped?.length ?? 0) > 0 && (
                            <Note tone="warn" title="Driver details were not imported">
                                Row {result.driversSkipped?.join(', row ')} carried a driver, but
                                importing uploaded numbers is switched off until migration 157 is
                                applied — without it an unverified number could be texted
                                automatically. The appointments were created; the drivers were not.
                            </Note>
                        )}

                        {/* ⭐ SAID BECAUSE A NUMBER APPEARED THAT NOBODY TYPED (#174).
                            ⚠️ Enrichment is the kind of helpfulness that looks like a
                            bug when it is silent — a scheduler comparing the sheet to
                            the appointment finds a PO on one and not the other. */}
                        {(result.wmsEnriched?.length ?? 0) > 0 && (
                            <p className="text-muted-foreground text-xs">
                                Row {result.wmsEnriched?.join(', row ')} matched an order in the WMS,
                                so its number was filled in on the appointment. Anything the sheet
                                already said was kept as written.
                            </p>
                        )}

                        {result.driversImported > 0 && (
                            <p className="text-muted-foreground text-xs">
                                {result.driversImported} driver contact(s) imported.
                                {' '}They will <strong>not</strong> be texted automatically until a
                                scheduler confirms the number.
                            </p>
                        )}

                        <DialogFooter>
                            <div className="flex w-full justify-end gap-2">
                                {!undone && (
                                    <Button variant="outline" size="sm" onClick={() => void doUndo()} disabled={busy}>
                                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                        <Undo2 className="h-3.5 w-3.5" /> Undo
                                    </Button>
                                )}
                                <Button size="sm" onClick={onClose} disabled={busy}>Close</Button>
                            </div>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

/** ⚠️ Never renders "null" or "undefined" into a cell. */
function str(v: unknown): string {
    return v === null || v === undefined ? '' : String(v);
}

/**
 * ⛔ THE REFERENCE COLUMN SHOWS WHICHEVER NUMBER THE ROW ACTUALLY CARRIES — SO IT
 * MUST BE TITLED BY WHAT IT IS SHOWING, NOT BY THE FALL-BACK CHAIN.
 *
 * Pete, 2026-08-24, looking at 41 Thornova rows: *"I think 'Reference' is suppose to
 * say 'ContainerID' right? The title is wrong for how it is mapped."* He is right.
 * Their file maps `Container#` and nothing else in this chain, so every value under
 * that heading was a container number while the heading said Reference.
 *
 * ⚠️ A COLUMN HEADING IS A CLAIM ABOUT EVERY CELL BENEATH IT. "Reference" over a
 * column of container numbers teaches a scheduler to read them as reference numbers —
 * and reference and container are different fields on the appointment, searched
 * differently and printed differently on the paperwork.
 *
 * ⭐ ORDER MATTERS AND MATCHES THE OLD CHAIN EXACTLY. Changing which value wins would
 * be a different fix from renaming the heading, and would silently alter what
 * hundreds of rows display.
 */
const REFERENCE_SOURCES: Array<{ key: string; label: string }> = [
    { key: 'poNumber',        label: 'PO' },
    { key: 'asnNumber',       label: 'ASN' },
    { key: 'containerNumber', label: 'Container' },
    { key: 'bolNumber',       label: 'BOL' },
    { key: 'referenceNumber', label: 'Reference' },
];

function referenceOf(values: Record<string, unknown> | null | undefined):
    { value: string; label: string | null } {
    for (const s of REFERENCE_SOURCES) {
        const v = str(values?.[s.key]);
        if (v) return { value: v, label: s.label };
    }
    return { value: '', label: null };
}

/**
 * ⭐ ONE HEADING WHEN EVERY ROW AGREES; the generic word when they do not.
 *
 * ⚠️ A MIXED FILE IS REAL — a broker's list can carry a PO on some rows and a
 * container on others — and inventing a single heading for it would be the same lie
 * in the other direction. ⛔ So "Reference" survives as the honest fallback rather
 * than being deleted.
 */
function referenceHeading(rows: Array<{ values?: Record<string, unknown> | null }>): string {
    const labels = new Set<string>();
    for (const r of rows) {
        const l = referenceOf(r.values).label;
        if (l) labels.add(l);
        if (labels.size > 1) return 'Reference';
    }
    const only = [...labels][0];
    return only ?? 'Reference';
}

/**
 * ⛔ AN ISO DAY IS NOT AN INSTANT, AND `new Date('2026-08-24')` PRETENDS IT IS.
 *
 * That form is parsed as UTC MIDNIGHT by spec. Rendered in any negative-offset zone
 * — which is every Symbia site — it comes out as the 23rd. ⚠️ The same class of bug
 * as #32 (*"every message quotes UTC, not facility-local time"*), one layer up: a
 * date the file plainly says is Monday would be offered as Sunday on the tick list,
 * and the day somebody ticks Sunday they import nothing and cannot see why.
 *
 * ⭐ Appending a time forces the local-midnight interpretation, which is what a bare
 * calendar day means to a person reading it.
 */
function dayLabel(iso: string): string {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * ⭐ THE TICK LIST OF DAYS THIS FILE HOLDS (#135).
 *
 * ⛔ CHIPS, NOT CHECKBOXES, and not because chips are prettier. There is no Checkbox
 * primitive in `components/ui`, and adding one for this would put a new dependency
 * and a new focus/keyboard contract in the tree for a control with three items. A
 * `<button aria-pressed>` is the same semantics with none of that.
 */
function DateScope(
    { seen, imported, rowsOtherDates, rowsNoDate, rowsBlankDateDropped, busy, onChange }: {
        seen: string[];
        imported: string[];
        rowsOtherDates: number;
        rowsNoDate: number;
        rowsBlankDateDropped: number;
        busy: boolean;
        onChange: (next: string[]) => void;
    },
) {
    /**
     * ⛔ NO FILTER READS AS EVERY DAY TICKED, because that is what it DID. An empty
     * `imported` with empty chips would tell a scheduler nothing was imported while
     * the row count below it says otherwise — the screen contradicting itself on the
     * one fact it exists to state.
     */
    const on = new Set(imported.length === 0 ? seen : imported);

    const toggle = (d: string) => {
        const next = new Set(on);
        if (next.has(d)) next.delete(d); else next.add(d);
        /**
         * ⛔ THE LAST TICK CANNOT BE REMOVED. An empty set means "no filter" to every
         * layer below this one — the API, the route and the reader all treat it as
         * "read the file as it is" — so emitting it here would silently import
         * EVERY day at the exact moment a scheduler was trying to import none.
         * ⚠️ The honest fix is to make the control refuse, not to invent an
         * "import nothing" that no layer supports.
         */
        if (next.size === 0) return;
        /**
         * ⭐ ALL DAYS TICKED CANONICALISES BACK TO "NO FILTER". The two are the same
         * request, and sending the explicit list would make an upload's stored
         * `RowsOtherDates` read 0-dropped-by-a-filter where nothing was filtered.
         */
        onChange(next.size === seen.length ? [] : [...next].sort());
    };

    return (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <div className="text-sm font-medium">
                This file holds {seen.length} days — choose which to import
            </div>
            <div className="flex flex-wrap gap-1.5">
                {seen.map(d => {
                    const active = on.has(d);
                    return (
                        <button
                            key={d} type="button" aria-pressed={active} disabled={busy}
                            onClick={() => toggle(d)}
                            className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors
                                disabled:opacity-50 ${active
                                    ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-700'
                                    : 'text-muted-foreground hover:bg-muted'}`}
                        >
                            {active && <CheckCircle2 className="mr-1 inline h-3 w-3" />}
                            {dayLabel(d)}
                        </button>
                    );
                })}
                {busy && <Loader2 className="mt-1.5 h-3.5 w-3.5 animate-spin" />}
            </div>
            {/* ⛔ NEVER SILENT. Taking Monday's 41 rows out of 1,208 is correct; doing
                it without a number on screen reads as "the upload worked" to somebody
                who is short a hundred trucks. */}
            <p className="text-muted-foreground text-xs">
                {rowsOtherDates > 0
                    ? `${rowsOtherDates} row(s) are for the days you have not ticked, and were not read.`
                    : 'Every row in this file is for a ticked day.'}
                {' '}Untick a day to leave it out — the last one cannot be unticked.
            </p>
            {/* ⛔ THE PART THE TICKS DO NOT COVER, SAID PLAINLY. A row with a blank or
                unreadable Date is imported whichever days are ticked — it takes the
                date at the top of the screen instead. ⚠️ Left unsaid, a scheduler who
                unticked everything but Monday would still get those rows and would
                have no way to work out why. */}
            {/* ⛔ THE UNSCHEDULED ROWS, SAID OUT LOUD AND NEVER SILENT.
                Pete's file, 2026-08-24: 41 rows dated 8/24 and 278 containers with no
                delivery date at all. Keeping those handed him 319 appointments when he
                expected 41 — *"There should only be 41 appointments for 8/24/2026."*
                ⚠️ It is often a LARGE number, so it must read as a deliberate exclusion
                rather than something that went missing. */}
            {rowsBlankDateDropped > 0 && (
                <p className="text-muted-foreground text-xs">
                    <strong>{rowsBlankDateDropped} row(s) have no delivery date</strong> in
                    this file, so they are not scheduled for any day and were not imported.
                </p>
            )}
            {/* ⚠️ DIFFERENT FROM THE ABOVE: the cell had something in it we could not
                read as a date. Those rows ARE imported — against the date at the top —
                so the judge can report them by row rather than them vanishing. */}
            {rowsNoDate - rowsBlankDateDropped > 0 && (
                <p className="text-muted-foreground text-xs">
                    {rowsNoDate - rowsBlankDateDropped} row(s) have a date we could not
                    read, so the ticks do not apply to them — they are imported against
                    the date at the top of this dialog.
                </p>
            )}
        </div>
    );
}

function Badge({ tone, children }: { tone: 'ok' | 'bad' | 'warn'; children: React.ReactNode }) {
    const cls = tone === 'ok'
        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700'
        : tone === 'bad'
            ? 'border-destructive/40 bg-destructive/10 text-destructive'
            : 'border-amber-500/40 bg-amber-500/10 text-amber-700';
    return <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

function Note(
    { tone, title, children }:
    { tone: 'bad' | 'warn'; title: string; children: React.ReactNode },
) {
    const bad = tone === 'bad';
    return (
        <div className={`flex gap-2 rounded-md border p-3 text-sm ${
            bad ? 'border-destructive/40 bg-destructive/10' : 'border-amber-500/40 bg-amber-500/10'}`}>
            {bad
                ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
            <div>
                <div className="font-medium">{title}</div>
                <div className="mt-0.5">{children}</div>
            </div>
        </div>
    );
}
